<template>
  <PriorHeader />
  <v-alert
    v-if="errorMsg"
    class="error-msg"
    :text="errorMsg"
    type="error"
    variant="outlined"
    :closable="true"></v-alert>
  <div class="d-flex flex-column align-center mt-8">
    <v-container :fluid="true" class="lh--container-wide">
      <v-row no-gutters>
        <v-col cols="12" class="patient-intake-container">
          <v-row no-gutters>
            <v-col cols="12" class="review-container">
              <!-- Adding a :key forces rerendering -->
              <v-row no-gutters>
                <v-col v-if="!showLoader">
                  <PatientBar
                    :patient="patientInfo"
                    :patient-guess="documentInfo.patientGuess"
                    :client-company="documentInfo.clientCompany"
                    :editable="!intakeStore.readOnly"
                    @change-attribute="onPatientAttributeChange"
                    @set-patient="onSetPatient"
                    @save-new-patient="onSaveNewPatient" />
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col v-if="!showLoader" cols="12">
                  <ClassificationBar :editable="!intakeStore.readOnly" :show-split-button="true" />
                </v-col>
                <v-container v-if="showLoader">
                  <v-skeleton-loader type="text@4" />
                </v-container>
              </v-row>
              <v-row v-if="intakeWarnings.length" no-gutters class="mb-2">
                <v-col v-for="(warning, index) in intakeWarnings" :key="`intake-warning-${index}`" cols="12">
                  <v-alert
                    class="intake-warning"
                    :text="warning.message"
                    :type="warning.type"
                    variant="tonal"
                    density="compact">
                  </v-alert>
                </v-col>
              </v-row>
              <PagePreview
                v-if="!showLoader && currentDocument && requirementsStore.clientConfig"
                ref="previewRef"
                :order-form="documentInfo.intakeOrderForm"
                :insurance-cards="documentInfo.intakeInsuranceCards"
                :to-dos="documentInfo.intakeToDos"
                @attribute-edit="onAttributeEdit"
                @add-content="onAddContent"
                @delete-content="onDeleteContent"
                @snooze="onSnooze"
                @archive="onArchive" />
              <v-container v-if="showLoader">
                <v-skeleton-loader type="image@12" />
              </v-container>
              <BottomBar
                :finish-review-enabled="finishReviewEnabled"
                intake-status="Intake"
                @submit-step="submitStep"
                @back-step="backStep">
                <v-row class="intake-footer">
                  <v-col cols="6">
                    <v-btn class="mr-2" @click="backStep">Go Back</v-btn>
                    <v-btn-lh style="min-width: 72px" variant="outlined" @click="downloadDocument">
                      <v-icon>mdi-download</v-icon>
                    </v-btn-lh>
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col v-if="!showLoader && !intakeStore.readOnly" cols="3">
                    <v-btn :disabled="buttonsDisabled" class="float-right" @click="submitStep">
                      {{ submitButtonText }}
                      &nbsp;<v-icon size="small">mdi-check</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </BottomBar>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row v-if="documentInfo">
        <v-col cols="12">
          <!-- Send Method Toggle (only shown when email is enabled) -->
          <v-btn-toggle
            v-if="sendEmailEnabled"
            v-model="sendMethod"
            mandatory
            class="mb-4"
            color="primary"
            variant="outlined">
            <v-btn value="fax">
              <v-icon start>mdi-fax</v-icon>
              Send Fax
            </v-btn>
            <v-btn value="email">
              <v-icon start>mdi-email</v-icon>
              Send Email
            </v-btn>
          </v-btn-toggle>

          <OutgoingFaxEditor v-if="sendMethod === 'fax'" :intake-document="documentInfo" title="Send Fax" />
          <OutgoingEmailEditor
            v-if="sendMethod === 'email'"
            :intake-document="documentInfo"
            :to-emails="referralSourceEmails"
            :default-subject="emailSubject"
            :template-context="emailTemplateContext"
            title="Send Email" />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <AuditLogList :intake-document-id="documentKey" @warning-note="onWarningNote" />
        </v-col>
      </v-row>
    </v-container>
    <v-dialog v-model="archiveReasonDialog" max-width="800">
      <ArchiveNote :intake-document="documentInfo" @on-archived="onArchived" />
    </v-dialog>
  </div>
  <PriorFooter />
</template>

<script setup>
import PriorHeader from "@/components/PriorHeader.vue";
import PriorFooter from "@/components/PriorFooter.vue";
import { onMounted, ref, watch, computed } from "vue";
import { onBeforeRouteLeave, useRoute } from "vue-router";
import { intakeService } from "@/services/intakeService";
import { useRequirementsStore } from "@/stores/requirementsStore";
import PagePreview from "@/components/intakeReview/PagePreview.vue";
import PatientBar from "@/components/intakeReview/PatientBar.vue";
import BottomBar from "@/components/intakeReview/BottomBar.vue";
import { IntakeDocumentPage, Patient, IntakeDocument, INTAKE_STAGE, DOCUMENT_STAGE } from "@/models/intake";
import router from "@/router";
import { patientService } from "@/services/patientService";
import debounce from "lodash.debounce";
import { useDate } from "vuetify";
import { useIntakeStore } from "@/stores/intakeStore";
import ClassificationBar from "@/components/intakeReview/ClassificationBar.vue";
import AuditLogList from "@/components/AuditLogList.vue";
import { getClientIntakeUI } from "@/clientSpecific/base/intakeUI";
import { handleFormValidationErrors } from "@/utils/validators";
import { useIntakeActions } from "@/composables/useIntakeActions";
import { useIntakeListFiltersStore } from "@/stores/intakeListFiltersStore";
import ArchiveNote from "@/components/ArchiveNote.vue";
import OutgoingFaxEditor from "@/components/OutgoingFaxEditor.vue";
import OutgoingEmailEditor from "@/components/OutgoingEmailEditor.vue";

const route = useRoute();
const dateAdapter = useDate();

const intakeStore = useIntakeStore();
const requirementsStore = useRequirementsStore();
const intakeActions = useIntakeActions();
const filtersStore = useIntakeListFiltersStore();

const documentInfo = computed(() => intakeStore.intakeDocument);
const patientInfo = ref(null);
const allPages = ref([]);
const reviewContainerHeight = ref("70vh");
const currentDocument = ref(null);
const finishReviewEnabled = ref(false);
const pageManager = intakeStore.pageManager;
const errorMsg = ref("");
const documentKey = route.params.id;
const previewRef = ref(null);
const showLoader = ref(true);
const buttonsDisabled = ref(false);
let lockInterval = null;
const patientKey = ref("-");
const archiveReasonDialog = ref(false);
const intakeUI = ref(null);
const sendMethod = ref("fax"); // 'fax' or 'email'

// Email feature flag from client config (camelized by API interceptor)
const sendEmailEnabled = computed(() => requirementsStore.clientConfig?.intake?.sendEmailEnabled ?? false);

// Get referral source emails from the intake order form
const referralSourceEmails = computed(() => {
  const orderForm = documentInfo.value?.intakeOrderForm;
  if (!orderForm?.extraData?.referralSourceEmail) {
    return [];
  }
  return [orderForm.extraData.referralSourceEmail];
});

// Generate email subject from patient info
const emailSubject = computed(() => {
  const patient = patientInfo.value;
  if (!patient) return "Document";
  const patientName = `${patient.firstName || ""} ${patient.lastName || ""}`.trim();
  return patientName ? `Document - ${patientName}` : "Document";
});

// Build template context for email template variable substitution
const emailTemplateContext = computed(() => {
  const orderForm = documentInfo.value?.intakeOrderForm;
  const insuranceCards = documentInfo.value?.intakeInsuranceCards || [];
  const toDos = documentInfo.value?.intakeToDos || [];
  const patient = patientInfo.value;

  return {
    patient: {
      firstName: patient?.firstName || "",
      lastName: patient?.lastName || "",
      middleName: patient?.middleName || "",
      dob: patient?.dobStr || "",
      phoneNumber: patient?.phoneNumber || "",
      address: patient?.address || "",
      externalId: patient?.externalId || "",
    },
    orderForm: {
      providerFirstName: orderForm?.providerFirstName || "",
      providerLastName: orderForm?.providerLastName || "",
      providerNpi: orderForm?.providerNpi || "",
      patientAddress: orderForm?.patientAddress || "",
      patientCity: orderForm?.patientCity || "",
      patientState: orderForm?.patientState || "",
      patientZip: orderForm?.patientZip || "",
      patientHomePhone: orderForm?.patientHomePhone || "",
      patientGender: orderForm?.patientGender || "",
      ...(orderForm?.extraData || {}),
    },
    insuranceCard:
      insuranceCards.length > 0
        ? {
            payorName: insuranceCards[0]?.payorName || "",
            memberId: insuranceCards[0]?.memberId || "",
            memberName: insuranceCards[0]?.memberName || "",
            insurancePlan: insuranceCards[0]?.insurancePlan || "",
            rxBin: insuranceCards[0]?.rxBin || "",
            rxPcn: insuranceCards[0]?.rxPcn || "",
            rxGrp: insuranceCards[0]?.rxGrp || "",
          }
        : {},
    toDo:
      toDos.length > 0
        ? {
            toDo: toDos[0]?.toDo || "",
            notes: toDos[0]?.notes || "",
            dueDate: toDos[0]?.dueDate || "",
            assignedTo: toDos[0]?.assignedTo || [],
          }
        : {},
  };
});

const updatePatientKey = () => {
  patientKey.value = `${patientInfo.value?.id}.${patientInfo.value?.modifiedOn}`;
};

watch(
  [documentInfo],
  ([newInfo]) => {
    showLoader.value = !newInfo;
  },
  { immediate: true }
);

// Sync patientInfo back to the store so other components can react to patient changes
watch(
  patientInfo,
  (newPatient) => {
    if (intakeStore.intakeDocument && newPatient) {
      intakeStore.intakeDocument.patient = newPatient;
    }
  },
  { deep: true }
);

onMounted(async () => {
  intakeStore.reset();
  intakeStore.stage = INTAKE_STAGE.INTAKE;
  fetchIntakeDocument().then(async () => {
    if (documentInfo.value) {
      await requirementsStore.refreshClientConfig(documentInfo.value.clientCompany);
      intakeUI.value = await getClientIntakeUI(requirementsStore.clientConfig, {}, intakeStore.stage);
    }
  });
  fetchIntakeDocumentPages();
  startStaffTime(route.params.id);
  window.addEventListener("beforeunload", onBeforeUnload);
});

const releaseLock = async () => {
  const documentKey = route.params.id;
  stopLockInterval();
  try {
    window.removeEventListener("beforeunload", onBeforeUnload);
    await intakeService.releaseIntakeDocumentLock(documentKey);
  } catch (e) {
    // Suppress
  }
};

onBeforeRouteLeave(async () => {
  await releaseLock();
  await updateStaffTime();
});

const onBeforeUnload = () => {
  // Fire and forget the save updates
  documentInfo.value?.saveUpdates();
  releaseLock();
  updateStaffTime();
};

const startStaffTime = (documentKey) => {
  try {
    intakeService.startIntakeDocumentStaffTime(documentKey);
  } catch (e) {
    // Suppress
  }
};

const updateStaffTime = async () => {
  const documentKey = route.params.id;
  try {
    await intakeService.pushIntakeDocumentStaffTime(documentKey);
  } catch (e) {
    // Suppress
  }
};

const startLockInterval = () => {
  if (lockInterval) return;

  lockInterval = setInterval(() => {
    try {
      intakeService.acquireIntakeDocumentLock(documentKey);
    } catch (e) {
      // Suppress
    }
  }, 60000);
};

const stopLockInterval = () => {
  clearInterval(lockInterval);
  lockInterval = null;
};

const onPatientAttributeChange = (name, value) => {
  patientInfo.value[name] = value;
  // Async and debounced method
  savePatientEdits();
};

const savePatientEdits = debounce(async () => {
  try {
    const response = await patientService.patchPatient(patientInfo.value.id, {
      firstName: patientInfo.value.firstName,
      lastName: patientInfo.value.lastName,
      phoneNumber: patientInfo.value.phoneNumber,
      dob: patientInfo.value.dob,
      middleName: patientInfo.value.middleName,
      suffix: patientInfo.value.suffix,
      externalId: patientInfo.value.externalId,
    });
    patientInfo.value = new Patient(response, dateAdapter);
    updatePatientKey();
  } catch (e) {
    errorMsg.value = `Error saving the patient: ${e.message}`;
    patientInfo.value = new Patient(documentInfo.value.patient, dateAdapter);
  }
}, 750);

const onSetPatient = async (patient) => {
  if (!patient) return;
  try {
    errorMsg.value = "";
    const response = await intakeService.patchIntakeDocument(documentKey, { patientId: patient.id });
    patientInfo.value = new Patient(response.patient, dateAdapter);

    // Call client-specific hook for patient selection
    await intakeUI.value?.onPatientSelected(patient, {
      documentInfo: documentInfo.value,
      intakeService,
      onAttributeEdit,
      fetchIntakeDocument,
    });
  } catch (e) {
    errorMsg.value = "Error saving patient to document";
  }
};

const onSaveNewPatient = async (newPatient) => {
  try {
    newPatient.clientCompany = documentInfo.value.clientCompany;
    const savedPatient = await patientService.postPatient(newPatient);
    patientInfo.value = new Patient(savedPatient, dateAdapter);
    await onSetPatient(savedPatient);
  } catch (e) {
    errorMsg.value = `Error saving the new patient: ${e.message}`;
  }
};

const submitStep = () => {
  onclickIntakeSubmit();
};

const backStep = async () => {
  router.push({ name: "all-patients", query: filtersStore.getNavigationQuery() });
};

const downloadDocument = async () => {
  window.open(documentInfo.value.inputPresignedUrl, "_blank");
};

const onAttributeEdit = (field, value, type, id) => {
  documentInfo.value.updateAttribute(field.key, value, type, id);
  documentInfo.value.debouncedSaveUpdates();
};

const errorWrapper = async (fn, errorPrefix) => {
  showLoader.value = true;
  let success = false;
  try {
    await fn();
    success = true;
  } catch (e) {
    errorMsg.value = `${errorPrefix} ${e.message}`;
  } finally {
    showLoader.value = false;
  }
  return success;
};

const onAddContent = async (page, documentType) => {
  if (!(await savePageContent())) {
    return;
  }
  await errorWrapper(async () => {
    await intakeActions.onAddContent(page, documentType);
    await fetchIntakeDocument();
  }, "Error adding the content");
};

const onDeleteContent = async (content, documentType) => {
  if (!(await savePageContent())) {
    return;
  }
  await errorWrapper(async () => {
    await intakeActions.onDeleteContent(content, documentType);
    await fetchIntakeDocument();
  }, "Error deleting the content");
};

const savePageContent = async () => {
  return await errorWrapper(async () => {
    await documentInfo.value.saveUpdates();
  }, "Error saving the page content");
};

const validateIntakeData = async () => {
  const formErrors = previewRef.value.formErrors;
  const isValid = await handleFormValidationErrors(formErrors);
  if (!isValid) {
    errorMsg.value = "There are some errors with the document data";
  }
  return isValid;
};

const onclickIntakeSubmit = async () => {
  if (buttonsDisabled.value) {
    return;
  }

  try {
    errorMsg.value = "";
    buttonsDisabled.value = true;
    await documentInfo.value.saveUpdates();

    if (!patientInfo.value) {
      errorMsg.value = "Please select a patient before continuing";
      return;
    }
    if (pageManager.pages?.some((page) => page.documentType === "")) {
      errorMsg.value = "Please classify all document pages before continuing";
      return;
    }
    // Client-specific validation
    const clientValidationError = intakeUI.value?.validateIntakeSubmit(
      patientInfo.value,
      documentInfo.value.intakeOrderForm
    );
    if (clientValidationError) {
      errorMsg.value = clientValidationError;
      return;
    }
    if (!(await validateIntakeData())) {
      return;
    }
    showLoader.value = true;
    const order = await intakeService.postIntakeDocumentSubmit(route.params.id);
    // If client configuration has inboxOnly flag, always route back to the inbox
    const inboxOnly = requirementsStore.clientConfig?.intake?.inboxOnly;
    if (inboxOnly) {
      router.push({ name: "all-patients", query: filtersStore.getNavigationQuery() });
      return;
    }
    if (order) {
      if (order.patient?.hasInsuranceCard) {
        router.push({ name: "qualify-order", params: { id: order.id } });
      } else {
        router.push({ name: "all-patients", query: filtersStore.getNavigationQuery() });
      }
    } else {
      router.push({ name: "all-patients", query: filtersStore.getNavigationQuery() });
    }
  } catch (ex) {
    errorMsg.value = `Error submitting the document: ${ex.message}`;
  } finally {
    showLoader.value = false;
    buttonsDisabled.value = false;
  }
};

const fetchIntakeDocumentPages = async () => {
  try {
    const documentKey = route.params.id;
    const response = await intakeService.getIntakeDocumentPages(documentKey);
    const pages = [];
    response.results.forEach((pageData) => {
      const page = new IntakeDocumentPage(pageData, dateAdapter);
      pages.push(page);
    });
    allPages.value = pages;
    pageManager.pages = pages;
    currentDocument.value = pages[0];
    reviewContainerHeight.value = "unset";
  } catch (e) {
    errorMsg.value = `Error fetching the document pages: ${e.message ?? e}`;
  }
};

const fetchIntakeDocument = async () => {
  const documentKey = route.params.id;
  try {
    await intakeService.acquireIntakeDocumentLock(documentKey);
    startLockInterval();
  } catch (e) {
    errorMsg.value = "Intake document is being edited by another user.";
    router.push({ name: "all-patients", query: filtersStore.getNavigationQuery() });
    return;
  }

  try {
    const response = await intakeService.getIntakeDocument(documentKey);
    intakeStore.intakeDocument = new IntakeDocument(response, dateAdapter);
    const patient = response.patient;
    if (patient) {
      patientInfo.value = new Patient(patient, dateAdapter);
    }
  } catch (e) {
    errorMsg.value = `Error fetching the document: ${e.message}`;
  }
};

const onSnooze = async ({ minutes }) => {
  await errorWrapper(async () => {
    intakeStore.intakeDocument = await intakeActions.onSnooze({ documentKey, minutes });
  }, "Error snoozing the document");
};

const onArchive = async () => {
  if (documentInfo.value.status === DOCUMENT_STAGE.ARCHIVED) {
    try {
      await intakeService.deleteArchiveDocument(documentKey);
      intakeStore.setReadOnly(false);
      return;
    } catch (e) {
      errorMsg.value = `Error unarchiving the document: ${e.message}`;
    }
  }
  archiveReasonDialog.value = true;
};
const onArchived = () => {
  archiveReasonDialog.value = false;
  router.push({ name: "all-patients", query: filtersStore.getNavigationQuery() });
};

const submitButtonText = computed(() => {
  return (
    intakeUI.value?.getInboxSubmitButtonText({
      orderForm: documentInfo.value.intakeOrderForm,
      patient: patientInfo.value,
    }) ?? "Submit"
  );
});

const intakeWarnings = computed(() => {
  if (!intakeUI.value) return [];
  return intakeUI.value.getIntakeWarnings({
    patient: patientInfo.value,
    pages: pageManager.pages,
  });
});

const onWarningNote = (warning) => {
  errorMsg.value = `Warning: ${warning.userNote?.note}`;
};
</script>

<style lang="scss" scoped>
@import "@/styles/pages/_patient-intake-review.scss";
</style>
